export interface DocumentStorage {
  id: string;
  title: string;
  description?: string;
  category: DocumentStorageCategory;
  customerId: string;
  customerName: string;
  folderId?: string; // 🚀 New folder assignment
  filePath?: string;
  fileSize?: number;
  fileType?: string;
  fileName?: string;
  downloadUrl?: string;
  tags?: string[];
  isPublic: boolean;
  creationDate?: Date;
  expirationDate?: Date;
  version: number;
  status: DocumentStorageStatus;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
  organizationId: string;
  metadata?: Record<string, any>;
  // 🚀 New lifecycle management fields
  lifecycle?: DocumentStorageLifecycle;
  storageOptimization?: {
    isOptimized: boolean;
    lastOptimizedAt?: Date;
    optimizationHistory: Array<{
      action: string;
      fromClass: string;
      toClass: string;
      savings: number;
      appliedAt: Date;
    }>;
  };
}

export interface DocumentStorageCategory {
  id: string;
  name: string;
  description?: string;
  color: string;
  icon: string;
  organizationId: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type DocumentStorageStatus = 'active' | 'archived' | 'expired' | 'draft';

// Storage lifecycle status
export type StorageLifecycleStatus = 'hot' | 'warm' | 'cold' | 'archive' | 'scheduled_deletion';

export interface DocumentStorageLifecycle {
  status: StorageLifecycleStatus;
  lastAccessed?: Date;
  accessCount: number;
  storageClass: 'STANDARD' | 'NEARLINE' | 'COLDLINE' | 'ARCHIVE';
  estimatedMonthlyCost: number; // USD
  potentialSavings: number; // USD if moved to cheaper tier
  nextTransition?: {
    toClass: 'NEARLINE' | 'COLDLINE' | 'ARCHIVE' | 'DELETE';
    scheduledDate: Date;
    estimatedSavings: number;
  };
  recommendations: string[];
}

export interface DocumentStorageFilter {
  customerId?: string;
  folderId?: string;
  folderIds?: string[]; // 🚀 For hierarchical folder search
  categoryId?: string;
  status?: DocumentStorageStatus;
  tags?: string[];
  searchTerm?: string;
  dateFrom?: Date;
  dateTo?: Date;
  // 🚀 New lifecycle filters
  lifecycleStatus?: StorageLifecycleStatus;
  storageClass?: 'STANDARD' | 'NEARLINE' | 'COLDLINE' | 'ARCHIVE';
  costRange?: {
    min: number;
    max: number;
  };
  optimizationOpportunity?: boolean; // Show only files with potential savings
  lastAccessedDays?: number; // Files not accessed in X days
}

export interface DocumentStorageFormData {
  title: string;
  description?: string;
  categoryId: string;
  customerId: string;
  tags?: string[];
  isPublic: boolean;
  creationDate?: Date;
  expirationDate?: Date;
  file?: File;
  folderId?: string; // 🚀 New folder support
}

// 🚀 New folder management types
export interface DocumentFolder {
  id: string;
  name: string;
  description?: string;
  parentId?: string; // null for root folders
  customerId?: string; // null for general folders
  customerName?: string;
  color: string;
  icon: string;
  organizationId: string;
  isDefault: boolean;
  isActive: boolean;
  documentCount: number;
  totalSize: number;
  createdBy: string;
  createdByName: string;
  createdAt: Date;
  updatedAt: Date;
  path: string[]; // breadcrumb path
  level: number; // folder depth
}

export interface FolderTreeNode extends DocumentFolder {
  children: FolderTreeNode[];
  isExpanded: boolean;
  isSelected: boolean;
}

// 🚀 Default document folders
export const DEFAULT_DOCUMENT_FOLDERS = [
  {
    name: 'Ügyfelek',
    description: 'Ügyfél-specifikus dokumentumok',
    color: '#3b82f6', // blue-500
    icon: 'UserGroupIcon',
    isDefault: true,
    level: 0
  }
];

// Default document storage categories
export const DEFAULT_DOCUMENT_STORAGE_CATEGORIES = [
  {
    name: 'Szerződések',
    description: 'Vevői szerződések és megállapodások',
    color: '#2563eb', // blue-600
    icon: 'DocumentTextIcon',
    isDefault: true
  },
  {
    name: 'Számlák',
    description: 'Kiállított számlák és bizonylatok',
    color: '#16a34a', // green-600
    icon: 'ReceiptPercentIcon',
    isDefault: true
  },
  {
    name: 'Ajánlatok',
    description: 'Árajánlatok és kalkulációk',
    color: '#ca8a04', // yellow-600
    icon: 'PresentationChartLineIcon',
    isDefault: true
  },
  {
    name: 'Garanciák',
    description: 'Garanciális dokumentumok',
    color: '#dc2626', // red-600
    icon: 'ShieldCheckIcon',
    isDefault: true
  },
  {
    name: 'Műszaki dokumentáció',
    description: 'Műszaki leírások és tervek',
    color: '#7c3aed', // purple-600
    icon: 'Cog6ToothIcon',
    isDefault: true
  },
  {
    name: 'Egyéb',
    description: 'Egyéb dokumentumok',
    color: '#6b7280', // gray-500
    icon: 'FolderIcon',
    isDefault: true
  }
];
